import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { DemoBadge } from "@/components/Badges";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Train, BedDouble, Utensils, CheckCircle2, XCircle, BadgeCheck, ShieldAlert,
  Accessibility as AccessIcon, Volume2, Ear, ParkingCircle, ShowerHead, Star,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { STAY_BADGE_LABELS } from "@/lib/constants";

const STAY_BADGE_ICON = {
  step_free_entrance: AccessIcon,
  roll_in_shower: ShowerHead,
  hearing_loop: Ear,
  accessible_parking: ParkingCircle,
};

export default function Accessibility() {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyMatches, setOnlyMatches] = useState(true);

  useEffect(() => {
    (async () => {
      const [cRes, tRes] = await Promise.all([api.get("/cities"), api.get("/trips")]);
      setCities(cRes.data);
      const defaultCity = tRes.data[0]?.destination_city || "London";
      setCity(defaultCity);
    })();
  }, []);

  useEffect(() => {
    if (!city) return;
    setLoading(true);
    api.get(`/accessibility/${encodeURIComponent(city)}`).then(({ data }) => {
      setData(data);
      setLoading(false);
    });
  }, [city]);

  const eat = data?.eat || [];
  const visibleEat = onlyMatches ? eat.filter((v) => v.matches_profile) : eat;

  return (
    <AppShell active="home" title="Accessibility explorer" showBack>
      <div className="space-y-1 mb-4">
        <div className="flex items-center gap-2">
          <Label className="text-[#A1A1AA]">Destination</Label>
        </div>
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger data-testid="access-city-select" className="min-h-[52px] rounded-xl bg-[#1A1A1A] text-base">
            <SelectValue placeholder="Choose a city" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {cities.map((c) => (
              <SelectItem key={c.city} value={c.city}>
                {c.city}, {c.country}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading || !data ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 bg-[#1A1A1A] rounded-2xl border border-[#333333] animate-pulse" />
          ))}
        </div>
      ) : (
        <Tabs defaultValue="transport">
          <TabsList className="grid grid-cols-3 w-full bg-[#222222] rounded-xl p-1 h-auto">
            <TabsTrigger data-testid="tab-transport" value="transport" className="min-h-[44px] rounded-lg data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-[#F8FAFC]">
              Transport
            </TabsTrigger>
            <TabsTrigger data-testid="tab-stay" value="stay" className="min-h-[44px] rounded-lg data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-[#F8FAFC]">
              Stay
            </TabsTrigger>
            <TabsTrigger data-testid="tab-eat" value="eat" className="min-h-[44px] rounded-lg data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-[#F8FAFC]">
              Eat & Do
            </TabsTrigger>
          </TabsList>

          {/* Transport */}
          <TabsContent value="transport" className="mt-4 space-y-3">
            <SourceBadge source="curated" />
            {[
              { icon: Train, t: "Step-free access", d: data.transport.step_free },
              { icon: AccessIcon, t: "Assistance booking", d: data.transport.assistance },
              { icon: Train, t: "Accessible taxis", d: data.transport.taxis },
            ].map((s, i) => (
              <div key={i} className="bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <s.icon size={18} className="text-[#EF9F27]" />
                  <h3 className="font-head font-bold text-[#F8FAFC]">{s.t}</h3>
                </div>
                <p className="text-[#F8FAFC] text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </TabsContent>

          {/* Stay */}
          <TabsContent value="stay" className="mt-4 space-y-3">
            <SourceBadge source={data.stay_source} />
            {data.stay.map((h, i) => (
              <div key={i} data-testid={`stay-card-${i}`} className="bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4">
                <Photo name={h.photo} alt={h.name} />
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-head font-bold text-[#F8FAFC]">{h.name}</h3>
                    <p className="text-sm text-[#A1A1AA]">{h.area}</p>
                    <Rating value={h.rating} count={h.user_ratings} />
                  </div>
                  {h.verified === "verified" ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#EF9F27] bg-[#EF9F27]/15 px-2 py-1 rounded-full">
                      <BadgeCheck size={14} /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#A1A1AA] bg-[#222222] px-2 py-1 rounded-full">
                      <ShieldAlert size={14} /> Unverified
                    </span>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {h.badges.length === 0 && (
                    <span className="text-sm text-[#666666]">No accessibility features listed</span>
                  )}
                  {h.badges.map((b) => {
                    const Icon = STAY_BADGE_ICON[b] || AccessIcon;
                    return (
                      <span key={b} className="inline-flex items-center gap-1 text-xs font-medium text-[#F8FAFC] bg-[#111111] border border-[#333333] px-2 py-1 rounded-full">
                        <Icon size={14} className="text-[#EF9F27]" /> {STAY_BADGE_LABELS[b]}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* Eat & Do */}
          <TabsContent value="eat" className="mt-4">
            <SourceBadge source={data.eat_source} />
            <div className="bg-[#1A1A1A] rounded-xl p-3 mb-3">
              <p className="text-sm text-[#F8FAFC]">
                Filtered for your profile:{" "}
                <span className="font-semibold">
                  {data.profile_summary.mobility_needs.replace(/_/g, " ")}
                </span>
                {data.profile_summary.sensory_needs.length > 0 &&
                  ` · ${data.profile_summary.sensory_needs.map((s) => s.replace(/_/g, " ")).join(", ")}`}
              </p>
            </div>
            <div className="flex items-center justify-between mb-3">
              <Label htmlFor="only-matches" className="text-[#F8FAFC]">Only show matches for me</Label>
              <Switch
                id="only-matches"
                data-testid="only-matches-switch"
                checked={onlyMatches}
                onCheckedChange={setOnlyMatches}
              />
            </div>

            <div className="space-y-3">
              {visibleEat.length === 0 && (
                <div className="bg-[#1A1A1A] border border-dashed border-[#333333] rounded-2xl p-6 text-center text-[#A1A1AA]">
                  No venues match your profile here. Turn off the filter to see all venues.
                </div>
              )}
              {visibleEat.map((v, i) => (
                <div
                  key={i}
                  data-testid={`venue-card-${i}`}
                  className={`bg-[#1A1A1A] border rounded-2xl p-4 ${
                    v.matches_profile ? "border-[#EF9F27]/40" : "border-[#333333]"
                  }`}
                >
                  <Photo name={v.photo} alt={v.name} />
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-head font-bold text-[#F8FAFC]">{v.name}</h3>
                      <p className="text-sm text-[#A1A1AA]">{v.kind}</p>
                      <Rating value={v.rating} count={v.user_ratings} />
                    </div>
                    {v.matches_profile ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-[#EF9F27]">
                        <CheckCircle2 size={16} /> Good for you
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-[#666666]">
                        <XCircle size={16} /> Check first
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#A1A1AA] mt-2">{v.note}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {v.wheelchair_access && <Feat icon={AccessIcon} label="Wheelchair access" />}
                    {v.accessible_toilet && <Feat icon={AccessIcon} label="Accessible toilet" />}
                    {v.quiet_space && <Feat icon={Volume2} label="Quiet space" />}
                    {v.braille_menu && <Feat icon={Utensils} label="Braille menu" />}
                  </div>
                  {v.match_reasons?.length > 0 && (
                    <p className="text-xs text-[#A1A1AA] mt-2">{v.match_reasons.join(" · ")}</p>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </AppShell>
  );
}

function Rating({ value, count }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1 text-sm text-[#A1A1AA] mt-0.5">
      <Star size={14} className="text-amber-400 fill-amber-400" />
      {value.toFixed(1)}
      {count ? <span className="text-[#666666]">({count.toLocaleString()})</span> : null}
    </span>
  );
}

function Photo({ name, alt }) {
  if (!name) return null;
  return (
    <img
      src={`${API}/place-photo?name=${encodeURIComponent(name)}&maxwidth=600`}
      alt={alt}
      loading="lazy"
      className="w-full h-32 object-cover rounded-xl mb-3"
    />
  );
}

function SourceBadge({ source }) {
  if (source === "live") {
    return (
      <span
        data-testid="source-badge-live"
        className="inline-flex items-center gap-1 bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest mb-1"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#EF9F27]" /> Live data · Google
      </span>
    );
  }
  if (source === "curated") {
    return (
      <span
        data-testid="source-badge-curated"
        className="inline-flex items-center bg-[#222222] text-[#A1A1AA] border border-[#333333] text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest mb-1"
      >
        Curated data
      </span>
    );
  }
  return <DemoBadge className="mb-1" />;
}

function Feat({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-[#F8FAFC] bg-[#111111] border border-[#333333] px-2 py-1 rounded-full">
      <Icon size={13} className="text-[#EF9F27]" /> {label}
    </span>
  );
}
